/**
 * @graphql agg
 */

import React, {
	useEffect,
	useLayoutEffect,
	useRef,
	useMemo,
	useState,
	useContext,
	useCallback,
} from 'react';
import { flushSync } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { graphql } from 'react-relay';

/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values */
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- This file still uses legacy xcss styles; compiled primitives treat those objects as class names and throw at runtime.
import Stack from '@atlaskit/primitives/stack';
import { xcss } from '@atlaskit/primitives/xcss';
import Box from '@atlaskit/primitives/box';

import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import {
	buildDefaultCommentsContentStatusFilters,
	type CommentData,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { CONTENT_STATUS_ARCHIVED } from '@confluence/archive-pages/entry-points/archivingConstants.ts';
import { PAGE_COMMENTS_METRIC } from '@confluence/comments-section/entry-points/pageSegmentLoadMetrics.ts';
import { CommentPlaceholder } from '@confluence/comment-simple/entry-points/CommentPlaceholder.ts';
import { ReactionsProvider } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import {
	useObjectCommentContext,
	useObjectCommentDispatchContext,
} from '@confluence/comment-context/entry-points/ObjectCommentContext.ts';
import { ObjectCommentQueryParamsListener } from '@confluence/comments-query-param-handlers/entry-points/ObjectCommentQueryParamsListener.ts';
import { useCommentsDataState } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import {
	getAnnotationThreadKeys,
	LocationValue,
	onlyHasReactionsSummaryErrors,
} from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { getVisibleCommentThreads } from '@confluence/comments-panel-utils/entry-points/getVisibleCommentThreads.ts';
import {
	AddFooterCommentMode,
	useCommentsPanelState,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import {
	getCommentPositionTopOfSectionEditor,
	getCommentPositionTopOfSectionRenderer,
	getWidthAndMaxWidth,
} from '@confluence/comments-util/entry-points/domUtils.ts';
import {
	getTranslatedError,
	isCommentAbortableError,
	isNetworkError,
	isViewNotPermittedError,
} from '@confluence/comments-util/entry-points/objectCommentsErrorUtils.ts';
import { objectCommentsi18n } from '@confluence/comments-util/entry-points/objectCommentsi18n.ts';
import { useContentState } from '@confluence/content-state/entry-points/useContentState.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { fg } from '@confluence/feature-gating';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import {
	ExperienceStart,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import {
	VIEW_PAGE_COMMENTS_EXPERIENCE,
	VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { LoadablePaint } from '@confluence/loadable';
import { useOmniBarVisibilityActions } from '@confluence/omni-bar-api/entry-points/OmniBarVisibilityStore.ts';
import { usePageContentId } from '@confluence/page-context';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { usePageRecommendationData } from '@confluence/page-recommendations/entry-points/usePageRecommendationData.tsx';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import { useUnreadComments } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { AgentSessionsFetcher } from '@confluence/agent-mention/entry-points/AgentSessionsFetcher.ts';
import { AGENT_MENTION_COMMENT_SAVED_EVENT } from '@confluence/agent-mention/entry-points/agentMentionConstants.ts';

import { usePageContentDimensions } from '../hooks/usePageContentDimensions.ts';
import { hideWhiteSpace } from '../utils/hideWhiteSpace.ts';
import {
	useViewPageConversationCommentThreads,
	ViewPageConversationDataProvider,
} from '../ViewPageConversationData.tsx';
import { buildPreloadObjectCommentsQueryVariables } from '../preloadObjectCommentsVariables.ts';

import { ObjectAddCommentContainer } from './ObjectAddCommentContainer.tsx';
import { ObjectCommentsList } from './ObjectCommentsList.tsx';
import { ObjectCommentHeader } from './ObjectCommentHeader.tsx';
import { ObjectAddCommentEditor } from './ObjectAddCommentEditor.tsx';
import type {
	ObjectCommentContainerPreloadObjectCommentsQuery$data,
	ObjectCommentContainerPreloadObjectCommentsQuery$variables,
} from './__generated__/ObjectCommentContainerPreloadObjectCommentsQuery.graphql.ts';
import {
	getPreloadAwareObjectComments,
	type UsePreloadAwareObjectCommentsReturn,
} from './helpers/ObjectCommentContainerHelpers.ts';

// GraphQL query for preloading first 3 footer comments
// NOTE: If you update the return type of this query, you need to update the type
// definition in any others as well, until our tooling supports fragments and then we move to a
// more fragmented-based approach.  Note there are also comment queries or fragments in the
// following files that need to be kept in sync as well:
// - next/packages/comments-data-manager/src/useAllCommentsData.ts
// - next/packages/comments-data/src/hooks/useCommentById.ts
// - next/packages/comments-data-manager/src/updateCommentInCache.ts
// - next/packages/comments-data-manager/src/useCreateObjectCommentMutation.ts
// - next/packages/comments-data-manager/src/useCreateInlineCommentMutation.ts
const ObjectCommentContainerPreloadObjectCommentsQuery = graphql`
	query ObjectCommentContainerPreloadObjectCommentsQuery(
		$pageId: ID!
		$contentStatus: [GraphQLContentStatus]
		$first: Long
		$includeCommentReactions: Boolean = true
		$includeConversationData: Boolean = false
		$includeCommentThreads: Boolean = false
		$focusedCommentId: ID
		$includeFocusedComment: Boolean = false
	) {
		...ViewPageConversation_currentUser
			@alias(as: "conversationCurrentUser")
			@include(if: $includeConversationData)
		content(id: $pageId, status: ["current", "archived", "draft"])
			@include(if: $includeConversationData) {
			nodes {
				...ViewPageConversation_contentPermission
			}
		}
		comments(
			pageId: $pageId
			contentStatus: $contentStatus
			depth: ROOT
			first: $first
			type: [FOOTER]
			confluenceCommentFilter: { commentState: UNRESOLVED, commentType: FOOTER }
		) {
			nodes {
				id
				author {
					__typename
					displayName
					permissionType
					... on KnownUser {
						accountId
						permissionType
					}
					profilePicture {
						path
					}
					type
				}
				contributors {
					__typename
					displayName
					profilePicture {
						path
					}
					... on KnownUser {
						accountId
					}
				}
				body(representation: ATLAS_DOC_FORMAT) {
					value
				}
				container {
					id
				}
				contentStatus
				createdAt {
					value
				}
				createdAtNonLocalized
				links {
					editui
					webui
				}
				location {
					__typename
					type
					... on FooterComment {
						commentResolveProperties {
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
					... on InlineComment {
						inlineMarkerRef
						inlineResolveProperties {
							isDangling
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
						inlineText
					}
				}
				parentId
				permissions {
					isEditable
					isRemovable
					isResolvable
				}
				...ViewPageConversation_commentReactions @relay(mask: false)
				replies {
					...ViewPageConversation_reply @relay(mask: false)
					contributors {
						__typename
						displayName
						profilePicture {
							path
						}
						... on KnownUser {
							accountId
						}
					}
				}
				version {
					by {
						__typename
						type
						displayName
						... on KnownUser {
							accountId
						}
					}
					friendlyWhen
					number
					when
				}
			}
			totalCount
			__typename
			...ViewPageConversation_commentThreads
				@alias(as: "conversationCommentThreads")
				@include(if: $includeCommentThreads)
		}
		focusedInlineComment: comments(commentId: $focusedCommentId, depth: ALL, type: UNRESOLVED)
			@include(if: $includeFocusedComment) {
			...ViewPageConversation_focusedInlineComment
		}
		...ViewPageConversation_focusedInlineCommentContext
			@alias(as: "focusedInlineCommentContext")
			@include(if: $includeFocusedComment)
	}
`;

const commentPanelContainerStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	height: '100%',
	width: '100%',
});

const wrapperStyle = xcss({
	position: 'relative',
	visibility: 'visible',
	minHeight: '50px',
	margin: '0 auto',
});

const baseEditorStyles = xcss({
	zIndex: 'modal',
	backgroundColor: 'elevation.surface',
	borderTopWidth: 'border.width',
	borderTopStyle: 'solid',
	borderColor: 'color.border',
	padding: 'space.200',
	position: 'relative',
});

const footerStickyWrapperStyle = xcss({
	zIndex: 'navigation',
	backgroundColor: 'elevation.surface',
	width: '100%',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	bottom: 'space.0',
});

const viewPageFooterStyle = xcss({
	zIndex: '1',
	transform: 'unset',
	bottom: 'space.0',
});

const footerEditorStyle = xcss({
	left: 'space.0',
});

const fixedEditorStyles = xcss({
	position: 'fixed',
});

const stickyEditorStyles = xcss({
	position: 'sticky',
});

const placeholderStyle = xcss({
	width: '60%',
});

const incrementalLoadingPlaceholderStyle = xcss({
	width: '60%',
});

// No border here: `ObjectCommentHeader` (has-comments) and the sticky add-comment bar
// (no-comments) each already draw their own top divider directly below this row.
const footerLabelsRowStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	paddingBottom: 'space.200',
});

// Same `webpackChunkName` as `ObjectAddCommentContainer`'s own copy of this loader, so no extra
// chunk is produced even though each file defines it separately.
const FooterLabelsLoader = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-FooterLabels" */ '@confluence/labels-footer'))
			.FooterLabels,
});

const addCommentContainerStyles = xcss({
	transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
	transformOrigin: 'top',
	position: 'sticky',
	bottom: 'space.0',
	zIndex: '1', // Required for create comment editor overflow to show on top of page content
});

const addCommentContainerAnimationHiddenStyles = xcss({
	transform: 'translateY(100%)',
	opacity: '0',
	pointerEvents: 'none',
});

const addCommentContainerAnimationShowStyles = xcss({
	transform: 'translateY(0)',
	opacity: '1',
	pointerEvents: 'auto',
});

type ObjectCommentContainerProps = {
	pageWidthType?: string;
	showLabels: boolean;
	hasReactions: boolean;
	editorIsUserOffline?: boolean;
	hasCommentSessions?: boolean;
};

const useFocusedCommentIdQueryParam = createSingleQueryParamHook('focusedCommentId');
const useReplyToCommentQueryParam = createSingleQueryParamHook('replyToComment');

const ObjectCommentContainerComponent = withFlags(
	({
		flags,
		pageWidthType,
		showLabels,
		hasReactions,
		editorIsUserOffline,
		hasCommentSessions,
	}: WithFlagsProps & ObjectCommentContainerProps) => {
		const [contentId] = usePageContentId();
		const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();

		// The footer mounts AgentSessionsFetcher only when the page-load `cc-agent-mention-sessions`
		// marker says sessions exist (hasCommentSessions, derived from ContentUnifiedQuery). But when
		// an agent is @-mentioned in a comment THIS session, the marker write (REST) isn't reflected
		// in the already-cached ContentUnifiedQuery, so hasCommentSessions stays false until a reload
		// and the footer never shows the new session. Optimistically flip this flag on the
		// comment-saved event so the fetcher mounts and its post-save poll surfaces the session
		// without a reload. (Panel/inline don't need this — they mount AgentSessionsProvider eagerly.)
		const [agentMentionedThisSession, setAgentMentionedThisSession] = useState(false);
		useEffect(() => {
			// Kill-switch: when the experiment is off, don't even register the listener — keep this
			// feature's production side effects fully gated, like the rest of it. The effect is still
			// called unconditionally (Rules of Hooks); only its body is guarded. expValEquals is
			// stable for the session, so reading it once on mount with empty deps is correct.
			if (!expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)) {
				return;
			}
			const handleAgentMentionCommentSaved = () => setAgentMentionedThisSession(true);
			window.addEventListener(AGENT_MENTION_COMMENT_SAVED_EVENT, handleAgentMentionCommentSaved);
			return () =>
				window.removeEventListener(
					AGENT_MENTION_COMMENT_SAVED_EVENT,
					handleAgentMentionCommentSaved,
				);
		}, []);

		const wrapperRef = useRef<HTMLDivElement>(null);
		const [commentSectionTop, setCommentSectionTop] = useState<number>(0);
		const [isInViewport, setIsInViewport] = useState(false);
		const [allCommentsRendered, setAllCommentsRendered] = useState(false);
		const [commentBatchRendered, setCommentBatchRendered] = useState(false);
		const [commentBatchCount, setCommentBatchCount] = useState(0);
		// Compatibility with the still-legacy query-param listener. The launched Data
		// Manager path owns loading, so this callback intentionally does nothing.
		const setForceLoadComments = (_value: boolean | ((currentValue: boolean) => boolean)) =>
			undefined;
		const [shouldShowAddComment, setShouldShowAddComment] = useState(true);
		const [state] = useContentState();
		const pageMode = useGetPageMode();
		const isServer = useIsSSROrHydrating();
		const { contentStatus } = useContentStatus();
		const { expanded, width, footerEditorEntry } = useObjectCommentContext();
		const { closeCommentContainer } = useObjectCommentDispatchContext();

		// Hide OmniBar when the footer comment component is open (e.g. Comment button clicked)
		const { requestHideOmniBar, releaseHideOmniBar } = useOmniBarVisibilityActions();
		useLayoutEffect(() => {
			if (expanded) {
				requestHideOmniBar();
				return () => releaseHideOmniBar();
			}
		}, [expanded, requestHideOmniBar, releaseHideOmniBar]);

		// Track page-content-only dimensions for layout alignment and blue background sizing
		const {
			width: pageContentWidth,
			left: pageContentLeft,
			right: pageContentRight,
		} = usePageContentDimensions();

		const { orderedActiveAnnotationIdList } = useCommentsDataState();

		const contentStatusFilters = buildDefaultCommentsContentStatusFilters(pageMode);

		// Get processed comments data
		const processedCommentsData = useProcessedCommentsDataState();
		const focusedCommentId = useFocusedCommentIdQueryParam();
		const replyToComment = useReplyToCommentQueryParam();

		// Read the preloaded footer comments from cache. Query for all content statuses; filtering
		// happens after the fact.
		//
		// Read through the React-context Relay environment so streaming SSR and client hydration use
		// the same cache. This is a non-suspending, standby read with no network request or subscription.
		const { data: hookPreloadCacheData } = useQueryNoFurtherUpdateInterop<
			ObjectCommentContainerPreloadObjectCommentsQuery$data,
			ObjectCommentContainerPreloadObjectCommentsQuery$variables
		>(ObjectCommentContainerPreloadObjectCommentsQuery, {
			variables: buildPreloadObjectCommentsQueryVariables(
				contentId ?? '',
				!StatsigConfigurations.isKillSwitchOn('confluence_vp_conversation_data_frag_reader') &&
					expVal('confluence_exp_conversation_contract', 'isEnabled', false),
				!StatsigConfigurations.isKillSwitchOn('confluence_vp_comment_threads_frag_reader') &&
					expVal('confluence_exp_conversation_contract', 'isEnabled', false),
				focusedCommentId,
				Boolean(focusedCommentId) &&
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_conversation_query') &&
					expVal('confluence_exp_conversation_contract', 'isEnabled', false),
			),
			skip: !contentId,
		});

		const commentThreadsFragmentData = useViewPageConversationCommentThreads(
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_comment_threads_frag_reader') &&
				expVal('confluence_exp_conversation_contract', 'isEnabled', false)
				? hookPreloadCacheData?.comments?.conversationCommentThreads
				: null,
		);

		const preloadCacheData: ObjectCommentContainerPreloadObjectCommentsQuery$data | null = useMemo(
			() =>
				commentThreadsFragmentData && hookPreloadCacheData
					? {
							...hookPreloadCacheData,
							comments: commentThreadsFragmentData,
						}
					: (hookPreloadCacheData ?? null),
			[commentThreadsFragmentData, hookPreloadCacheData],
		);

		const preloadAwareFooterDataResult = useMemo<UsePreloadAwareObjectCommentsReturn>(
			() =>
				getPreloadAwareObjectComments(
					processedCommentsData,
					preloadCacheData,
					contentStatusFilters,
				),
			[processedCommentsData, preloadCacheData, contentStatusFilters],
		);

		const [shouldRequeryArchivedComments, setShouldRequeryArchivedComments] = useState(false);
		const hasRequeriedArchivedComments = useRef(false);
		const startedExperience = useRef(false);

		const {
			replyToCommentId,
			currentlySelectedThreadKey,
			location: commentLocation,
		} = useCommentsPanelState();

		const [{ isUnreadQueryLoading }] = useUnreadComments();
		const [hasLoadedUnreadStatus, setHasLoadedUnreadStatus] = useState(false);
		const experienceTracker = useContext(ExperienceTrackerContext);

		const isContentReady = pageMode === PageMode.VIEW ? state.isContentReady : true;

		// Check if the page is archived
		const isArchived = contentStatus === CONTENT_STATUS_ARCHIVED;

		// Hide add comment container on archived pages
		useEffect(() => {
			if (isArchived) {
				setShouldShowAddComment(false);
				setShouldRequeryArchivedComments(true);
			}
		}, [isArchived]);

		// Close comment container when expanded and contentId changes
		useEffect(() => {
			if (expanded && contentId) {
				closeCommentContainer();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [contentId]);

		// AKA - has the user scrolled to the bottom of the page?
		const isContentInViewport = isInViewport && isContentReady;

		// When preloading, we don't always have access to archived page state (depends on a race condition for cache hit / miss)
		// In those cases, we can incorrectly determine that the page is fully loaded with no comments, when really we just queried using the wrong vars
		// Given that archived are such a small percent of loads, just force a reload here (i.e. don't trust cache) to be safe
		const shouldForceReloadForArchivedComments =
			shouldRequeryArchivedComments && !hasRequeriedArchivedComments.current;

		const wasFullyLoaded =
			preloadAwareFooterDataResult.isUsingFullData || preloadAwareFooterDataResult.allDataPreloaded;

		const wasPartiallyPreloaded = preloadAwareFooterDataResult.isPreloadDataProcessed;

		const { hasRecs } = usePageRecommendationData();

		const error = preloadAwareFooterDataResult.error;
		const data = useMemo(
			() => ({
				comments: {
					totalCount: preloadAwareFooterDataResult.commentCount,
					nodes: preloadAwareFooterDataResult.openFooterComments,
				},
			}),
			[preloadAwareFooterDataResult.commentCount, preloadAwareFooterDataResult.openFooterComments],
		);

		// Enhanced loading state that accounts for data manager processing
		// Only show loading if we're actually loading OR if data isn't processed yet
		const loading =
			preloadAwareFooterDataResult.isPreloadLoading ||
			!preloadAwareFooterDataResult.isDataProcessed;

		// In cases where the first paint will be correct (i.e. will match SPA version because nothing controlled by a useEffect / DOM action), immediately render
		// Otherwise, this will just happen on first paint in SPA once the useEffects run
		const renderOnServer = isServer && data?.comments?.totalCount === 0;

		// TODO: Needs investigation; ignored because linting rules changed during feature gate cleanup.
		const noOpenObjectComments = useMemo(() => {
			return data?.comments?.nodes?.length === 0 || true;
			// oxlint-disable-next-line react-hooks/exhaustive-deps -- Preserve existing loading behavior; investigate separately.
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-usememo-deps
		}, [data]);

		// If we're forcing a reload for archived comments, we need to set the flag to true so we don't try to reload again
		useEffect(() => {
			if (shouldForceReloadForArchivedComments && !loading && data) {
				hasRequeriedArchivedComments.current = true;
			}
		}, [data, loading, shouldForceReloadForArchivedComments]);

		// AKA - are we done loading?
		const shouldShowComments = wasFullyLoaded || wasPartiallyPreloaded || renderOnServer;

		// Return null when we haven't loaded anything yet, we're not in the viewport, and we're not SSRed
		const shouldReturnNull =
			(error && !onlyHasReactionsSummaryErrors(error)) ||
			(!isContentInViewport && !shouldShowComments);

		// Show global placeholder (i.e. instead of comments header, etc) when we're in the viewport but are still loading, and we're not SSRed
		const shouldShowGlobalPlaceholder = isContentInViewport && !shouldShowComments;

		// Show incremental loading placeholder in SSR when preload is completed with comments
		// We can't show the comments themselves because commentThreads is set across multiple renders via sweet state
		// This also ensures that we either show the loading placeholder or the comment threads, never both
		// Without it, we can briefly show both threads & the placeholder, even if fully loaded
		// UPDATE: SSRing the incremental loading placeholder seems to slow down rendering of the
		// preloaded top footer comments on browser, leading to an increase in ViewPage VC90. Thus,
		// `confluence_fe_ssr_top_footer_comments` experiment disables it and instead renders the
		// actual top footer comments on server-side.
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		const shouldShowIncrementalLoadingPlaceholderOnServer =
			isServer &&
			preloadAwareFooterDataResult?.preloadCompletedWithComments &&
			!expValEquals('confluence_fe_ssr_top_footer_comments', 'isEnabled', true);

		// Show placeholder when we're in the viewport but are still loading the rest of the data
		const shouldShowIncrementalLoadingPlaceholder =
			(shouldShowComments && loading && !wasFullyLoaded) ||
			shouldShowIncrementalLoadingPlaceholderOnServer;

		// Set up IntersectionObserver
		useEffect(() => {
			if (isServer || !isContentReady) {
				// Don't register observer if page content is not ready
				return;
			}

			// eslint-disable-next-line no-restricted-syntax
			const observer = new IntersectionObserver(
				([entry]) => {
					const isIntersecting = entry.isIntersecting;
					if (isIntersecting) {
						setIsInViewport(isIntersecting);
					}
				},
				{
					root: null,
					rootMargin: '10px',
					threshold: 0.1,
				},
			);

			if (wrapperRef.current) {
				observer.observe(wrapperRef.current);
			}

			return () => {
				observer.disconnect();
			};
		}, [isServer, pageMode, state.isContentReady, isContentReady]);

		// Add MutationObserver to detect when comments are actually rendered --- old logic for checking all comment rendered or not
		useEffect(() => {
			if (isServer || !currentlySelectedThreadKey) {
				return;
			}

			const observer = new MutationObserver(() => {
				const commentElementId = `footer-comment-thread-${currentlySelectedThreadKey}-container`;
				const commentElement = document.getElementById(commentElementId);

				if (commentElement) {
					setAllCommentsRendered(true);
					observer.disconnect(); // Disconnect once we find the element
				} else {
					setAllCommentsRendered(false); // Reset if element not found
				}
			});

			observer.observe(document.body, {
				childList: true,
				subtree: true,
			});

			// Reset allCommentsRendered when unmounting or changing thread key
			return () => {
				setAllCommentsRendered(false);
				observer.disconnect();
			};
		}, [currentlySelectedThreadKey, isServer]);

		useEffect(() => {
			if (!isUnreadQueryLoading && !hasLoadedUnreadStatus) {
				setHasLoadedUnreadStatus(true);
			}
		}, [isUnreadQueryLoading, hasLoadedUnreadStatus]);

		// Handle spacing for Editor & LiveDoc modes by observing the size of editorTextArea
		useEffect(() => {
			const editorTextArea = document.getElementById('ak-editor-textarea');
			if (editorTextArea) {
				if (
					// eslint-disable-next-line confluence-feature-gating/no-preconditioning
					hasRecs &&
					expValEquals('confluence_ai_related_content_for_live_pages', 'isEnabled', true) &&
					fg('confluence_frontend_unify_footer_comment_space_ld')
				) {
					setCommentSectionTop(0);
					return;
				}

				const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
					const newTop = getCommentPositionTopOfSectionEditor(entries);
					flushSync(() => {
						newTop !== undefined && setCommentSectionTop(newTop);
					});
				});
				// observe resize of editorTextArea to update spacing when content changes
				resizeObserver.observe(editorTextArea);
				// observe resize of document.body to update spacing when window height changes
				resizeObserver.observe(document.body);
				return () => resizeObserver.disconnect();
			}
		}, [hasRecs]);

		// Handle spacing for Renderer mode based on content and window height
		useEffect(() => {
			const renderContentContainer = document.querySelector(
				'[data-testid="content-container-component"]',
			);
			if (
				pageMode === PageMode.VIEW &&
				renderContentContainer &&
				fg('confluence_frontend_unify_footer_comment_space_old')
			) {
				const resizeObserver = new ResizeObserver((_entries: ResizeObserverEntry[]) => {
					const newTop = getCommentPositionTopOfSectionRenderer();
					flushSync(() => {
						setCommentSectionTop(newTop);
					});
				});
				// observe resize of content-container-component to update spacing when content changes
				// (the container includes page title, content, and end-of-page recommendations components, etc.)
				resizeObserver.observe(renderContentContainer);
				// observe resize of document.body to update spacing when window height changes
				resizeObserver.observe(document.body);
				return () => {
					resizeObserver.disconnect();
				};
			}
		}, [pageMode]);

		const commentsDataMap = preloadAwareFooterDataResult.commentsDataMap;

		const commentThreads = useMemo(() => {
			if (!wasFullyLoaded && !shouldShowComments) {
				// no need to update if we haven't loaded, or aren't showing comments
				return [];
			}

			return getVisibleCommentThreads({
				commentsDataMap,
				includeGeneralComments: true,
				includeInlineComments: false,
				orderedAnnotationIds: getAnnotationThreadKeys(orderedActiveAnnotationIdList),
				pageMode,
			});
		}, [
			wasFullyLoaded,
			shouldShowComments,
			commentsDataMap,
			orderedActiveAnnotationIdList,
			pageMode,
		]);

		const hasComments = commentThreads.length > 0;

		// Handle add comment container animation and mounting
		useEffect(() => {
			if (isArchived) {
				return;
			}
			const shouldHide = !!replyToCommentId && commentLocation === LocationValue.FOOTER;

			if (shouldHide) {
				// Start hiding animation
				setShouldShowAddComment(false);
			} else {
				// Show component after a small delay to allow for animation
				const timer = setTimeout(() => {
					setShouldShowAddComment(true);
				}, 50);
				return () => clearTimeout(timer);
			}
		}, [replyToCommentId, commentLocation, isArchived]);

		// Track the start of comments loading and initialize performance metrics
		useEffect(() => {
			const shouldStartExperience =
				!startedExperience.current &&
				((isContentInViewport && loading) || (wasFullyLoaded && hasComments));
			if (shouldStartExperience) {
				// Start tracking the page comments experience with initial attributes
				experienceTracker.start({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					attributes: {
						isConditionalRenderingOn: true,
						isFocusedComment: Boolean(focusedCommentId || replyToComment),
						contentId,
						pageMode,
					},
				});
				startedExperience.current = true;

				// Initialize and mark the start of page comments rendering metrics
				PAGE_COMMENTS_METRIC.start();
				PAGE_COMMENTS_METRIC.mark('pageCommentsRenderStart');
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [
			shouldShowIncrementalLoadingPlaceholder,
			shouldShowGlobalPlaceholder,
			loading,
			isContentInViewport,
			wasFullyLoaded,
			hasComments,
		]);

		// Track any errors that occur during comments loading
		useEffect(() => {
			if (error) {
				if (onlyHasReactionsSummaryErrors(error)) {
					experienceTracker.updateAttributes({
						name: VIEW_PAGE_COMMENTS_EXPERIENCE,
						attributes: {
							ignoredReactionsSummaryErrors: true,
						},
					});
					// Comments may still be displayed without reactions, so do not
					// fail or abort the experience but just return here.
					return;
				}

				const translatedError = getTranslatedError(error);

				// The experiment is evaluated first so the broader abortable-error set (which also
				// covers the step-up-auth and IP-allowlist access-policy errors) is only used while
				// the experiment is on; when it is off the behaviour is unchanged.
				if (
					expValEquals('confluence_fe_abort_comment_on_access_policy_err', 'isEnabled', true)
						? isCommentAbortableError(translatedError)
						: isViewNotPermittedError(translatedError)
				) {
					// If the error is a permission or access-policy error, show a warning flag and abort the experience
					void flags.showWarningFlag({
						title: <FormattedMessage {...objectCommentsi18n.unableToLoadCommentsTitle} />,
						description: <FormattedMessage {...translatedError} />,
					});

					experienceTracker.abort({
						name: VIEW_PAGE_COMMENTS_EXPERIENCE,
						reason: translatedError.id,
					});
					PAGE_COMMENTS_METRIC.cancel();
					return;
				}

				if (isNetworkError(translatedError)) {
					// Show flag for network error
					void flags.showWarningFlag({
						title: <FormattedMessage {...objectCommentsi18n.unableToLoadCommentsTitle} />,
						description: <FormattedMessage {...translatedError} />,
					});
					// The experience will be aborted by experienceTracker.stopOnError() below.
				}

				// Mark the experience as failed/aborted and include the error details
				experienceTracker.stopOnError({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					error,
				});
				PAGE_COMMENTS_METRIC.cancel();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [error]);

		// Track successful completion of comments rendering
		useEffect(() => {
			if (allCommentsRendered && wasFullyLoaded && hasComments) {
				// Mark the experience as successful and include total comments count
				experienceTracker.succeed({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					attributes: {
						totalComments: data?.comments?.totalCount,
						totalCommentThreads: commentThreads.length,
					},
				});

				// Mark key performance metrics:
				// - pageCommentsRenderEnd: End of comments rendering
				PAGE_COMMENTS_METRIC.mark('pageCommentsRenderEnd');
				// Stop the metric tracking and include final data
				PAGE_COMMENTS_METRIC.stop({
					customData: {
						contentId: contentId || '',
						totalComments: data?.comments?.totalCount || 0,
					},
				});
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [allCommentsRendered, wasFullyLoaded, hasComments]);

		// Track successful completion of first comment batch rendering
		useEffect(() => {
			if (commentBatchRendered) {
				// Mark key performance metrics:
				// - fmp: First Meaningful Paint
				// - tti: Time to Interactive
				PAGE_COMMENTS_METRIC.markFMP();
				PAGE_COMMENTS_METRIC.mark('tti');

				const experienceState = experienceTracker.getExperienceState(VIEW_PAGE_COMMENTS_EXPERIENCE);

				if (experienceState?.startTime && typeof experienceState.startTime === 'number') {
					experienceTracker.updateAttributes({
						name: VIEW_PAGE_COMMENTS_EXPERIENCE,
						attributes: {
							firstMeaningfulPaint: window.performance.now() - experienceState.startTime,
						},
					});
				}
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [commentBatchRendered]);

		// Handle case when there are no comments to render
		useEffect(() => {
			if (data?.comments?.totalCount === 0) {
				// Abort the experience tracking since there are no comments to render
				experienceTracker.abort({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					reason: 'No page comments',
				});
				PAGE_COMMENTS_METRIC.cancel();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [data]);

		const handleAllCommentsRendered = useCallback(() => setAllCommentsRendered(true), []);

		const computedWidths = getWidthAndMaxWidth({
			pageMode,
			pageWidthType,
			hasEndOfPageRecommendations: hasRecs ?? false,
		});

		const shouldRenderFixedStyle = useMemo(() => {
			if (expanded && footerEditorEntry === AddFooterCommentMode.STICKY_DRAFT) {
				return true;
			}
			return false;
		}, [expanded, footerEditorEntry]);

		const pageContentDimensions = useMemo(
			() => ({
				width: pageContentWidth,
				left: pageContentLeft,
				right: pageContentRight,
			}),
			[pageContentWidth, pageContentLeft, pageContentRight],
		);

		// AgentSessionsFetcher is only mounted when the experiment is on — this guarantees
		// zero Relay queries and zero Suspense when the gate is off.
		const content = (
			<Box
				ref={wrapperRef}
				xcss={wrapperStyle}
				testId="object-comment-wrapper"
				style={{
					top: commentSectionTop,
					width: computedWidths.width,
					maxWidth: computedWidths.maxWidth,
					// display: 'contents', // So wrapper doesn't affect layout if empty
				}}
				data-vc={`object-comment-wrapper-${pageMode}-${hasComments ? 'has-comments' : 'no-comments'}`}
			>
				<>
					<ObjectCommentQueryParamsListener
						hasLoadedObjectComments={!!shouldShowComments}
						allObjectCommentsRendered={
							allCommentsRendered || noOpenObjectComments || wasFullyLoaded
						}
						commentBatchRendered={commentBatchRendered}
						commentBatchCount={commentBatchCount}
						setForceLoadComments={setForceLoadComments}
					/>
					{shouldReturnNull ? null : (
						<>
							{shouldShowGlobalPlaceholder && (
								<Box xcss={placeholderStyle}>
									<CommentPlaceholder showPlaceholder />
								</Box>
							)}
							{shouldShowComments && (
								<ReactionsProvider contentId={contentId ?? ''}>
									{/* One placement regardless of has-comments/no-comments, directly above the
									comments-section divider (the has-comments heading's top border, or the
									sticky add-comment bar's top border when there are no comments yet). */}
									{
										// eslint-disable-next-line confluence-feature-gating/no-preconditioning
										showLabels &&
											fg('confluence_frontend_labels_in_footer') &&
											expValEquals('confluence_footer_reactions_redesign', 'isEnabled', true) && (
												<Box xcss={footerLabelsRowStyles}>
													<ExperienceStart
														id={contentId}
														name={VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE}
													/>
													<FooterLabelsLoader />
												</Box>
											)
									}
									{hasComments && (
										<ObjectCommentHeader
											isOffline={editorIsUserOffline}
											showReactions={isEmbeddedConfluence ? hasReactions : true}
										/>
									)}
									<Stack xcss={commentPanelContainerStyles}>
										{!shouldShowIncrementalLoadingPlaceholderOnServer && (
											<ObjectCommentsList
												commentThreads={commentThreads as unknown as CommentData[]}
												supportedTopLevelActions={['edit', 'resolve', 'delete']}
												flags={flags}
												onAllCommentsRendered={handleAllCommentsRendered}
												onCommentBatchRendered={() => {
													setCommentBatchRendered(true);
													setCommentBatchCount((prev) => prev + 1);
												}}
												isOffline={editorIsUserOffline}
											/>
										)}
										{shouldShowIncrementalLoadingPlaceholder && (
											<Box xcss={incrementalLoadingPlaceholderStyle}>
												<CommentPlaceholder showPlaceholder />
											</Box>
										)}
									</Stack>
									<Box
										xcss={[
											addCommentContainerStyles,
											!!replyToCommentId && commentLocation === LocationValue.FOOTER
												? addCommentContainerAnimationHiddenStyles
												: addCommentContainerAnimationShowStyles,
										]}
									>
										{!shouldShowGlobalPlaceholder &&
											!shouldShowIncrementalLoadingPlaceholder &&
											shouldShowAddComment &&
											!isArchived &&
											!expanded && (
												<ObjectAddCommentContainer
													showReactions={
														!hasComments && (isEmbeddedConfluence ? hasReactions : true)
													}
													showLabels={showLabels}
													isOffline={editorIsUserOffline}
													optimisticReactions
												/>
											)}
									</Box>
								</ReactionsProvider>
							)}
						</>
					)}
				</>
				{/* Editor when expanded - fixed for stickyDraft, sticky for others */}
				{expanded && (
					<Box
						xcss={[
							footerStickyWrapperStyle,
							shouldRenderFixedStyle ? fixedEditorStyles : stickyEditorStyles,
							pageMode === PageMode.VIEW && viewPageFooterStyle,
							pageMode !== PageMode.VIEW && footerEditorStyle,
						]}
						testId="footerCommentEditor"
						style={
							pageMode === PageMode.VIEW
								? hideWhiteSpace({
										shouldRenderFixedStyle,
										pageContentDimensions,
										fallbackWidth: width,
									})
								: undefined
						}
					>
						<Box
							xcss={baseEditorStyles}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
							style={{
								width,
								maxWidth: computedWidths.maxWidth,
							}}
						>
							<ObjectAddCommentEditor isOffline={editorIsUserOffline} />
						</Box>
					</Box>
				)}
			</Box>
		);

		// Mount the fetcher when the page-load marker says there are sessions OR an agent was
		// mentioned this session (the marker isn't yet reflected in the cached ContentUnifiedQuery).
		// hasCommentSessions already includes the experiment gate; gate the optimistic branch on the
		// experiment too so it stays a kill-switch. Ordered last per confluence-feature-gating rules.
		const renderWithAgentSessions = (renderedContent: React.ReactNode) => {
			if (
				hasCommentSessions ||
				(agentMentionedThisSession &&
					expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true))
			) {
				const mountedFromLocalMention = agentMentionedThisSession && !hasCommentSessions;
				return (
					<AgentSessionsFetcher
						pageId={contentId ?? ''}
						refetchOnMount={mountedFromLocalMention}
						preserveEmptyMarker={mountedFromLocalMention}
					>
						{renderedContent}
					</AgentSessionsFetcher>
				);
			}

			return renderedContent;
		};

		if (
			preloadCacheData?.content?.nodes?.[0] &&
			preloadCacheData.conversationCurrentUser &&
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_conversation_data_frag_reader') &&
			expVal('confluence_exp_conversation_contract', 'isEnabled', false)
		) {
			return renderWithAgentSessions(
				<ViewPageConversationDataProvider
					contentPermissionFragmentRef={preloadCacheData.content.nodes[0]}
					currentUserFragmentRef={preloadCacheData.conversationCurrentUser}
				>
					{content}
				</ViewPageConversationDataProvider>,
			);
		}

		return renderWithAgentSessions(content);
	},
);

export const ObjectCommentContainer: React.FC<Omit<ObjectCommentContainerProps, 'flags'>> =
	withErrorBoundary({
		attribution: Attribution.COMMENTS,
	})(ObjectCommentContainerComponent);
